import type { Metric, PeriodResult, Provenance } from './engine';

export type ChangeStatus = 'ok' | 'cannot' | 'unavailable';

export interface ComparisonRow {
  key: string;
  label: string;
  unit: string;
  decimals: number;
  current: Metric;
  reference: Metric;
  absChange: number | null;
  pctChange: number | null;
  ppChange: number | null;
  status: ChangeStatus;
  message?: string;
  isShare: boolean;
  isIllustrative: boolean;
  provenance?: Provenance;
}

/**
 * Periodeendring = (valgt / referanse − 1) × 100 når referanse > 0.
 * Andeler sammenlignes også i prosentpoeng. Nullgrunnlag gir «kan ikke beregnes».
 */
export function compareMetric(current: Metric, reference: Metric): ComparisonRow {
  const base: ComparisonRow = {
    key: current.key,
    label: current.label,
    unit: current.unit,
    decimals: current.decimals,
    current,
    reference,
    absChange: null,
    pctChange: null,
    ppChange: null,
    status: 'unavailable',
    isShare: !!current.isShare,
    isIllustrative: !!current.isIllustrative,
    provenance: current.provenance,
  };
  const cv = current.value;
  const rv = reference.value;
  if (current.status === 'unavailable' || reference.status === 'unavailable' || cv === null || rv === null) {
    return {
      ...base,
      status: 'unavailable',
      message:
        current.status === 'unavailable'
          ? `Valgt periode: ${current.message ?? 'utilgjengelig'}`
          : `Referanse: ${reference.message ?? 'utilgjengelig'}`,
    };
  }
  const abs = cv - rv;
  if (current.isShare) {
    return {
      ...base,
      absChange: abs,
      ppChange: abs,
      pctChange: rv > 0 ? (cv / rv - 1) * 100 : null,
      status: 'ok',
      message: rv > 0 ? undefined : 'Relativ endring kan ikke beregnes (referanse 0). Prosentpoeng vises.',
    };
  }
  if (!(rv > 0)) {
    return { ...base, absChange: abs, status: 'cannot', message: 'Kan ikke beregnes: referanseverdien er 0.' };
  }
  return { ...base, absChange: abs, pctChange: (cv / rv - 1) * 100, status: 'ok' };
}

export interface PeriodComparison {
  current: PeriodResult;
  reference: PeriodResult;
  rows: ComparisonRow[];
  byKey: Record<string, ComparisonRow>;
  caveats: string[];
}

export function comparePeriods(current: PeriodResult, reference: PeriodResult): PeriodComparison {
  const pairs: [Metric, Metric][] = [
    [current.tonnes, reference.tonnes],
    [current.fuelLitres, reference.fuelLitres],
    [current.engineHours, reference.engineHours],
    [current.idleHours, reference.idleHours],
    [current.productionHours, reference.productionHours],
    [current.stopHours, reference.stopHours],
    [current.dieselPerTonne, reference.dieselPerTonne],
    [current.machineHoursPerTonne, reference.machineHoursPerTonne],
    [current.idleShare, reference.idleShare],
    [current.productionRate, reference.productionRate],
    [current.costPerTonne, reference.costPerTonne],
    [current.co2TotalKg, reference.co2TotalKg],
    [current.co2PerTonne, reference.co2PerTonne],
  ];
  const rows = pairs.map(([c, r]) => compareMetric(c, r));
  const byKey: Record<string, ComparisonRow> = {};
  for (const r of rows) byKey[r.key] = r;
  const caveats: string[] = [
    'Ulik produktmiks (fraksjoner) og ulikt driftsmønster (skift, stopp) kan påvirke sammenligningen.',
  ];
  if (current.productionDays !== reference.productionDays) {
    caveats.push(`Antall registrerte produksjonsdager er ulikt (${current.productionDays} mot ${reference.productionDays}).`);
  }
  if (!current.basisComplete || !reference.basisComplete) {
    caveats.push('Minst én av periodene har ufullstendig grunnlag; berørte forholdstall er ikke sammenlignet.');
  }
  return { current, reference, rows, byKey, caveats };
}
